import { getSketchById } from '@/lib/canvas/shader/library'
import { declaredShaderInputs } from '@/lib/canvas/configs/handles'
import {
  ShaderGenerationError,
  type ShaderAuthoringState,
  type ShaderGenerationErrorCode,
  type ShaderGenerationMode,
  type ShaderGenerationResult,
  type ShaderPromptEntry,
  type ShaderProviderConfig,
} from '@/lib/canvas/shader/authoring/types'
import type { ShaderInputType } from '@/lib/canvas/shader/media/contract'

type State = Record<string, unknown>

/** How many applied prompts a node remembers. */
export const PROMPT_HISTORY_LIMIT = 20

/** Titles a user did not choose: none, the factory's "Shader node 3", or an applied library sketch's name. */
export function isDefaultShaderTitle(title: unknown, sketchId: unknown): boolean {
  if (typeof title !== 'string' || !title.trim()) return true
  if (/^Shader( node)?( \d+)?$/.test(title.trim())) return true
  const sketch = typeof sketchId === 'string' ? getSketchById(sketchId) : undefined
  return sketch?.name === title
}

/** Inputs the node's current code reads. */
export function shaderStateInputTypes(state: State): ShaderInputType[] {
  return declaredShaderInputs(
    typeof state.sketchId === 'string' ? state.sketchId : undefined,
    Array.isArray(state.inputTypes) ? state.inputTypes : undefined,
  )
}

export interface AppliedGeneration {
  result: ShaderGenerationResult
  prompt: string
  mode: ShaderGenerationMode
  config: ShaderProviderConfig
  entryId: string
  /** ISO time of the submission. Tests pin it; the node uses now. */
  at?: string
}

/**
 * The single state change a successful generation makes, so it is exactly one
 * undo step: new code and its inputs and params, the prompt recorded and cleared.
 */
export function withGeneratedShader(prev: State, { result, prompt, mode, config, entryId, at }: AppliedGeneration): State {
  const previousValues = (prev.paramValues as State | undefined) ?? {}
  // Keep a value the user set for a param that survived the edit, if it still fits its type.
  const paramValues = Object.fromEntries(
    result.params.map((param) => {
      const kept = previousValues[param.name]
      return [param.name, kept !== undefined && typeof kept === typeof param.default ? kept : param.default]
    }),
  )
  const history = Array.isArray(prev.promptHistory) ? (prev.promptHistory as ShaderPromptEntry[]) : []
  const { sketchId, ...rest } = prev
  // An edit renames the node too while its title is still the one the model gave it;
  // a title the user typed stays.
  const lastGeneratedTitle = history.at(-1)?.title
  const retitle = mode === 'create' || isDefaultShaderTitle(prev.title, sketchId) || (!!lastGeneratedTitle && prev.title === lastGeneratedTitle)
  const title = retitle && result.title ? result.title : prev.title
  const entry: ShaderPromptEntry = {
    id: entryId,
    prompt,
    mode,
    provider: config.provider,
    model: config.model,
    summary: result.summary,
    at: at ?? new Date().toISOString(),
    code: result.code,
    runtimeMode: result.runtimeMode,
    params: result.params,
    paramValues: paramValues as ShaderPromptEntry['paramValues'],
    ...(typeof title === 'string' && title ? { title } : {}),
  }

  const next: State & ShaderAuthoringState = {
    ...rest,
    code: result.code,
    runtimeMode: result.runtimeMode,
    inputTypes: result.inputTypes,
    extractedParams: result.params,
    paramValues,
    title,
    generation: { status: 'idle', summary: result.summary },
    promptHistory: [...history, entry].slice(-PROMPT_HISTORY_LIMIT),
    prompt: '',
  }
  return next
}

export interface GenerationFailure {
  message: string
  code: ShaderGenerationErrorCode
}

export function toGenerationFailure(error: unknown): GenerationFailure {
  if (error instanceof ShaderGenerationError) return { message: error.message, code: error.code }
  if (error instanceof DOMException && error.name === 'AbortError') return { message: 'Cancelled', code: 'ABORTED' }
  if (error instanceof Error) return { message: error.message || 'Shader generation failed', code: 'UNKNOWN' }
  return { message: 'Shader generation failed', code: 'UNKNOWN' }
}

/** A failed generation records why and keeps the prompt; the code that was running stays untouched. */
export function withGenerationError(
  prev: State,
  failure: GenerationFailure,
  prompt: string,
  details?: { config: ShaderProviderConfig; mode: ShaderGenerationMode; entryId: string; at?: string },
): State {
  const generation = { status: 'error' as const, error: failure.message, code: failure.code }
  if (!details) return { ...prev, prompt, generation }
  const history = Array.isArray(prev.promptHistory) ? (prev.promptHistory as ShaderPromptEntry[]) : []
  const entry: ShaderPromptEntry = {
    id: details.entryId,
    prompt,
    mode: details.mode,
    provider: details.config.provider,
    model: details.config.model,
    summary: failure.message,
    error: failure.message,
    at: details.at ?? new Date().toISOString(),
  }
  return {
    ...prev,
    prompt,
    generation,
    promptHistory: [...history, entry].slice(-PROMPT_HISTORY_LIMIT),
  }
}

/** Puts one of this shader's revisions back. Other shaders' history is not in this state. */
export function restoreShaderRevision(prev: State, entry: ShaderPromptEntry): State {
  if (!entry.code) return prev
  const { sketchId: _sketchId, ...rest } = prev
  return {
    ...rest,
    code: entry.code,
    runtimeMode: entry.runtimeMode ?? prev.runtimeMode,
    extractedParams: entry.params ?? prev.extractedParams,
    paramValues: entry.paramValues ?? prev.paramValues,
    prompt: entry.prompt,
    previewPaused: false,
    generation: { status: 'idle', summary: entry.summary },
  }
}
